import { AbstractControl, ReactiveFormsModule, UntypedFormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { combineLatest, of, Subject, timer, Subscription, filter, switchMap, take, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, input, model, OnDestroy, OnInit, PLATFORM_ID,
  viewChild, ViewEncapsulation
} from '@angular/core';

import { CmsService, Page } from '@spartacus/core';

import {
  DeviceService, IconSizeEnum, IMedia, INewsletter, ISdFormComponentItem, SdBannerComponent, SdButtonComponent,
  SdFormCheckboxComponent, SdFormErrorComponent, SdIconComponent, SdLoaderComponent, SdModalDefaultComponent,
  SdTitleComponent, StatusEnum
} from '@seb/web-design-system/core';

import {
  BaseStoreService, DynamicFormService, HobStateService, ImageService, InteractionsService,
  NewsletterService, ToasterService
} from '@seb/web-common/core/services';

import { BaseNewsletterComponent } from '../../../base/base-newsletter/base-newsletter.component';
import { CaptchaTurnstileComponent } from '../captcha-turnstile/captcha-turnstile.component';
import { NewsletterComponentInterface, NewsletterResponse } from '../../../../models';
import { mapPageTemplateToPageType } from '../../../../utils/pages-type.utils';
import { TrackingService } from '../../../../services/tracking.service';
import { NewsLetterStep } from '../../../../enums/newsletter-steps';

export interface LabeledControl extends AbstractControl {
  name: string;
  label: string;
  required: boolean;
}

@Component({
  selector: 'wc-popin-newsletter',
  templateUrl: './popin-newsletter.component.html',
  styleUrls: ['./popin-newsletter.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdModalDefaultComponent,
    SdBannerComponent,
    BaseNewsletterComponent,
    CaptchaTurnstileComponent,
    TranslateModule,
    SdFormErrorComponent,
    SdFormCheckboxComponent,
    SdTitleComponent,
    SdIconComponent,
    SdLoaderComponent,
    SdButtonComponent,
    ReactiveFormsModule
  ]
})
export class PopinNewsletterComponent implements OnInit, OnDestroy {
  private readonly baseStoreService = inject(BaseStoreService);
  private readonly cmsService = inject(CmsService);
  private readonly translateService = inject(TranslateService);
  private readonly imageService = inject(ImageService);
  private readonly router = inject(Router);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly newsletterService = inject(NewsletterService);
  private readonly toasterService = inject(ToasterService);
  private readonly trackingService = inject(TrackingService);
  private readonly deviceService = inject(DeviceService);
  private readonly dynamicFrom = inject(DynamicFormService);
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly hobStateService = inject(HobStateService);
  private readonly interactionsService = inject(InteractionsService);
  private readonly platformId = inject(PLATFORM_ID);

  popinTimeOut = model(1500);
  isOpened = model(false);
  isCheckout = input(false);

  isMobile = toSignal(this.deviceService.isMobile);

  recaptcha = viewChild(CaptchaTurnstileComponent);

  formError = false;
  newsletterContext = 'Popin';
  successMessage = '';
  imageNewsletter: IMedia;
  isLoading = false;
  newsletterData: INewsletter = null;
  trackingContext = '';
  formSuccess = false;
  formNewsletter: FormGroup;
  dynamicTermsGroup: FormGroup;

  legalFormGroup = new FormGroup({
    formCheckbox: new FormControl(false, Validators.requiredTrue)
  });

  contactGroupName: string;
  termsConditionGroupName: string;
  additionalInfoGroupName: string;
  titleLevel = 2;
  IconSizeEnum = IconSizeEnum;
  currentStep = NewsLetterStep.STEP_REGISTER;
  form = {};
  gender: ISdFormComponentItem[];

  private isNewsletterOpened = false;
  private useDataQualityFormValidation = false;
  private emailScore: string;
  private emailScoreModifiedDate: string;
  private isDisplayNewsletterEventSent = false;
  private isClosedNewsletterEventSent = false;
  private isHob: boolean;

  protected subscriptions = new Subscription();

  private timeOut$ = new Subject<boolean>();

  constructor() {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
  }

  ngOnInit(): void {
    const newsletterCmp$ = this.cmsService.getContentSlot('Newsletter').pipe(
      switchMap((component) =>
        !!component.components && component.components.length > 0
          ? this.cmsService.getComponentData(component.components[0].uid)
          : of(null)
      ),
      take(1)
    );
    const page$ = this.cmsService.getCurrentPage().pipe(filter((page) => !!page));
    const hasBeenClosed = localStorage.getItem('newsletterRegistrationPopupClosed');
    const subscribed = localStorage.getItem('newsletter_subscribed');
    const canOpenModal = (!hasBeenClosed && !this.isCheckout()) || (this.isCheckout() && !subscribed);
    const baseStore = this.baseStoreService.baseStore;

    this.subscriptions.add(
      this.hobStateService.isHob$.subscribe((isHob) => {
        this.isHob = isHob;
      })
    );

    this.subscriptions.add(
      combineLatest([newsletterCmp$, page$])
        .subscribe(([newsletter, page]: [NewsletterComponentInterface, Page]) =>
        {
          this.popinTimeOut.set(!this.isCheckout() && baseStore?.newsletterAppearanceTimer * 1000);
          this.useDataQualityFormValidation = baseStore.enableDQE;
          const pageType = mapPageTemplateToPageType(page, this.router.parseUrl(this.router.url));
          const isExcluded = newsletter?.excludedPageTypes
            ?.split(' ')
            ?.some((excludedType) => excludedType?.includes(pageType) && page.label !== '/order-confirmation');

          if (!isExcluded && canOpenModal && baseStore.displayNewsletterRegistrationPopup && !!newsletter) {
            this.imageNewsletter = newsletter.media ? this.imageService.normalizeMedia(newsletter.media) : undefined;
            this.newsletterData = {
              subtitle: newsletter.subline,
              checkboxLabel: newsletter.tickMarkText,
              thanksSubtitle: newsletter.sublineSuccess,
              newsletterSubscribe: this.translateService.instant('newsletterRegistrationPopup.validateButton')
            };
            this.successMessage = newsletter.headlineSuccess;
            this.newsletterContext = this.isCheckout() ? 'Guest_checkout' : newsletter.optinContext;

            this.interactionsService.waitForUserInteraction().subscribe((interacted) => {
              if (interacted) {
                this.startNewsletterPopinTimer();
              }
            });
          } else if (!this.isCheckout()) {
            this.timeOut$.next(true); // Emits a value to cancel the timer
          }

          if (!this.formNewsletter) {
            this.initForm();
          }

          this.cdRef.markForCheck();
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private initForm() {
    this.subscriptions.add(
      this.dynamicFrom
        .getNewsLetterBySite()
        .subscribe((newsLetterForm) => {
          this.formNewsletter = this.formBuilder.group({});
          newsLetterForm.formAttributeSections.forEach((group) => {
            this.formNewsletter.addControl(group.code, this.formBuilder.group({}));
            if (group.code.includes('contactGroup') && !!group?.formAttributes.length) {
              this.contactGroupName = group.code;
            } else if (group.code.includes('termsGroup') && !!group?.formAttributes.length) {
              this.termsConditionGroupName = group.code;
            } else if (group?.formAttributes.length) {
              this.additionalInfoGroupName = group.code;
            }
            group?.formAttributes.forEach((input) => {
              const nameInput = input.originalName || input.name;
              const isMandatoryText = !!input.mandatory && input.type === 'TEXT';
              const isEmail = nameInput === 'email';
              const isGender = nameInput === 'gender';
              const isPhone = nameInput === 'phone';
              const isDate = nameInput === 'birthDay';
              const isHouseHoldSize = nameInput === 'houseHoldSize';
              const controlGroup = this.formNewsletter.get(group.code) as FormGroup;

              if (group.code.includes('termsGroup')) {
                this.dynamicTermsGroup = controlGroup;
                controlGroup.addControl(nameInput, new FormControl(false, Validators.requiredTrue));
                (controlGroup.get(nameInput) as any).name = nameInput;
                (controlGroup.get(nameInput) as any).label = input.label;
                return;
              }

              const validators = [];

              if (isMandatoryText) {
                validators.push(Validators.required);
              }

              if (isEmail) {
                validators.push(
                  Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$'),
                  Validators.minLength(4)
                );
              } else if ((!this.useDataQualityFormValidation && isPhone) || (!isPhone && !isGender && !isHouseHoldSize)) {
                validators.push(
                  Validators.pattern(input.regexp),
                  Validators.maxLength(+input.length),
                );
              } else if (isHouseHoldSize) {
                validators.push(Validators.min(1), Validators.max(99));
              }

              const value = isGender ? { libelle: '', value: '' } : isHouseHoldSize ? 1 : '';
              controlGroup.addControl(nameInput, new FormControl(value, validators));
              (controlGroup.get(nameInput) as any).type = isGender
                ? 'radio'
                : isEmail
                  ? 'email'
                  : isHouseHoldSize
                    ? 'number'
                    : isDate
                      ? 'date'
                      : 'text';
              (controlGroup.get(nameInput) as any).inline =
                nameInput === 'firstName' ||
                nameInput === 'lastName' ||
                nameInput === 'postalCode' ||
                nameInput === 'city';
              (controlGroup.get(nameInput) as any).name = nameInput;
              (controlGroup.get(nameInput) as any).label = input.label;
              (controlGroup.get(nameInput) as any).regexErrorMessage = input.regexErrorMessage;

              switch (nameInput) {
                case 'houseHoldSize':
                  (controlGroup.get(nameInput) as any).subLabel = this.translateService
                    .instant('newsletter.subLabel.' + nameInput, { emoji: '🏡' })
                    .trim();
                  break;
                case 'birthDay':
                  (controlGroup.get(nameInput) as any).subLabel = this.translateService
                    .instant('newsletter.subLabel.' + nameInput, { emoji: '🎂' })
                    .trim();
                  break;
                case 'phone':
                  (controlGroup.get(nameInput) as any).subLabel = this.translateService
                    .instant('newsletter.subLabel.' + nameInput, { emoji: '📱' })
                    .trim();
                  break;
                case 'postalCode':
                case 'city':
                  (controlGroup.get(nameInput) as any).subLabel = this.translateService
                    .instant('newsletter.subLabel.' + nameInput, { emoji: '🛍️' })
                    .trim();
                  break;
              }
              (controlGroup.get(nameInput) as any).placeholder = input.placeholder;
            });
          });

          this.translateService
            .get(['newsletter.genre.male', 'newsletter.genre.female', 'newsletter.genre.notSay'])
            .subscribe((results) => {
              this.gender = [
                {
                  libelle: results['newsletter.genre.male'],
                  value: 'male'
                },
                {
                  libelle: results['newsletter.genre.female'],
                  value: 'female'
                },
                {
                  libelle: results['newsletter.genre.notSay'],
                  value: undefined
                }
              ];
            });
        })
    );
  }

  private handleRegisterStepResponse(response: NewsletterResponse) {
    if (response?.profile?.success === true && response?.newsletters?.success === true) {
      this.trackingService.sendEvent('newsletterSubscription', { position: this.trackingContext });

      if (this.formNewsletter.get(this.additionalInfoGroupName)) {
        this.currentStep = NewsLetterStep.STEP_ADDITIONAL_INFO;
      } else {
        this.currentStep = NewsLetterStep.STEP_THANKYOU;
      }
    } else {
      this.notifySubscriptionError();
    }
  }

  private handleAdditionalInfoStep() {
    this.trackingService.sendEvent('newsletterAdditionalInfo', { position: this.trackingContext });
    this.currentStep = NewsLetterStep.STEP_THANKYOU;
  }

  private notifySubscriptionError() {
    this.toasterService.sendToasterEvent({
      status: StatusEnum.error,
      title: 'notify.notifyErrorToastTitle',
      description: 'httpErrors.500'
    });
  }

  private startNewsletterPopinTimer() {
    timer(this.popinTimeOut()).pipe(
      takeUntil(this.timeOut$)
    ).subscribe(() => {
      if (!this.isCheckout()) {
        this.isOpened.set(!localStorage.getItem('newsletterRegistrationPopupClosed'));
      } else {
        this.isOpened.set(true);
      }

      this.isNewsletterOpened = true;
      this.trackingContext = this.isCheckout() ? 'Popin guest checkout' : 'Popin first visit';

      if (!!this.isOpened() && !this.isDisplayNewsletterEventSent) {
        this.trackingService.sendEvent('newsletterDisplay', {
          popinType: this.trackingContext
        });
        this.isDisplayNewsletterEventSent = true;
      }
      this.cdRef.detectChanges();
    });
  }

  private onSubmitNewsletterModal(form: string | any): void {
    this.form = form;
    this.recaptcha()?.execute();
  }

  onSubmit() {
    const phoneValue = this.formNewsletter.get(this.contactGroupName)?.get('phone')?.value;
    const smsTermCondition = (!!phoneValue && this.dynamicTermsGroup?.get('termsConditionSms')?.valid) || !phoneValue;

    if (this.currentStep === NewsLetterStep.STEP_REGISTER) {
      if (
        this.legalFormGroup.get('formCheckbox').valid &&
        smsTermCondition &&
        this.formNewsletter.get(this.contactGroupName).valid
      ) {
        this.isLoading = true;
        this.onSubmitNewsletterModal({
          email: this.formNewsletter.get(this.contactGroupName).get('email').value,
          emailQualityScore: this.emailScore,
          emailQualityScoreModifiedDate: this.emailScoreModifiedDate,
          firstName: this.formNewsletter.get(this.contactGroupName).get('firstName').value,
          lastName: this.formNewsletter.get(this.contactGroupName).get('lastName').value,
          phone: this.formNewsletter.get(this.contactGroupName)?.get('phone')?.value,
          gender: this.formNewsletter.get(this.contactGroupName).get('gender').value.value,
          prefComSMS: this.dynamicTermsGroup?.get('termsConditionSms')?.value,
          prefComEmail: this.legalFormGroup.get('formCheckbox').value
        });
      } else {
        this.formNewsletter?.get(this.contactGroupName)?.markAllAsTouched();
        this.dynamicTermsGroup?.markAllAsTouched();
        this.legalFormGroup?.markAllAsTouched();
      }
    } else if (this.currentStep === NewsLetterStep.STEP_ADDITIONAL_INFO) {
      if (this.formNewsletter.get(this.additionalInfoGroupName).valid) {
        this.isLoading = true;
        const birthDate = this.formNewsletter.get(this.additionalInfoGroupName).get('birthDay').value;
        const city = this.formNewsletter.get(this.additionalInfoGroupName).get('city').value;
        const postalCode = this.formNewsletter.get(this.additionalInfoGroupName).get('postalCode').value;
        const houseHoldSize = this.formNewsletter.get(this.additionalInfoGroupName).get('houseHoldSize').value;
        this.form = {
          ...this.form,
          birthDate: birthDate,
          city: city,
          postalCode: postalCode,
          householdMembers: houseHoldSize
        };
        this.subscribeToNewsletter(this.currentStep);
      } else {
        this.formNewsletter.get(this.additionalInfoGroupName).markAllAsTouched();
      }
    } else if (this.currentStep === NewsLetterStep.STEP_THANKYOU) {
      this.onCloseModal();
    }
  }

  saveClosed(isOpened: boolean): void {
    if (!isOpened && this.isNewsletterOpened) {
      localStorage.setItem('newsletterRegistrationPopupClosed', JSON.stringify(true));
      if (!this.isClosedNewsletterEventSent) {
        this.trackingService.sendEvent('newsletterClose', { position: this.trackingContext });
        this.isClosedNewsletterEventSent = true;
      }
    }
  }

  onCloseModal(): void {
    if (this.form) {
      this.form = {};
    }
    this.isOpened.set(false);
  }

  subscribeToNewsletter(currentStep: NewsLetterStep): void {
    this.subscriptions.add(
      this.newsletterService
        .subscribeToNewsletter(this.form, this.newsletterContext, this.isHob)
        .subscribe((response) => {
          this.isLoading = false;
          if (response) {
            if (currentStep === NewsLetterStep.STEP_REGISTER) {
              this.handleRegisterStepResponse(response);
            }
            else if (currentStep === NewsLetterStep.STEP_ADDITIONAL_INFO) {
              this.handleAdditionalInfoStep();
            }
            sessionStorage.setItem('newsletter_subscription', 'true');
          }
          else {
            this.notifySubscriptionError();
          }
          this.cdRef.markForCheck();
        })
    );
  }

  captchaError(): void {
    this.isLoading = false;
  }

  populateEmailScore($event: string) {
    this.emailScore = $event;
    const today = new Date();
    this.emailScoreModifiedDate = today.toISOString().split('T')[0];
  }

  getControls(group: FormGroup) {
    return group?.controls ? Object.values(group.controls) as LabeledControl[] : [];
  }
}
